import { Component, ElementRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';

interface FileProps {
  label: string;
  accept?: string;
  required?: boolean;
}

@Component({
  selector: 'formly-field-ion-file',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './custom-ion-file.component.html',
  styleUrls: ['./custom-ion-file.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IonicModule,
    TranslocoModule,
  ],
})
export class CustomIonFileComponent extends FieldType<FieldTypeConfig<FileProps>> {
  constructor(protected translocoService: TranslocoService) {
    super();
  }

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  override get formControl(): FormControl {
    return this.field.formControl as FormControl;
  }

  get displayFileName(): string {
    const value = this.formControl.value;
    if (value instanceof File) {
      return value.name;
    }
    return '';
  }

  onFileChange(event: Event): void {
    const target: HTMLInputElement = event.target as HTMLInputElement;
    const files: FileList | null = target.files;

    const maxSizeMB: number = 5;
    const maxSizeBytes: number = maxSizeMB * 1024 * 1024;

    if (files && files.length > 0) {
      const file: File = files[0];

      if (file.size > maxSizeBytes) {
        this.formControl.setValue(null);
        this.formControl.setErrors({ fileSize: true });
      } else {
        this.formControl.setValue(file);
        this.formControl.setErrors(null);
      }
    } else {
      this.formControl.setValue(null);
      this.formControl.setErrors(null);
    }

    this.formControl.markAsDirty();
    this.formControl.markAsTouched();
  }

  override get showError(): boolean {
    return (
      !!this.formControl &&
      this.formControl.invalid &&
      (this.formControl.touched ||
        (this.options.parentForm as any)?.submitted)
    );
  }

  get errorMessage(): string | null {
    const errors = this.formControl?.errors;
    if (!errors) return null;

    if (errors['fileSize']) {
      return this.translocoService.translate('FORMLY.ERROR.FILE_SIZE');
    }

    return this.translocoService.translate('FORMLY.ERROR.FIELD_REQUIRED');
  }
}